<!--~
~ Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
~ to an End-User License Agreement included in a separate file named EULA.txt
~ that is distributed with this file.
-->
@if (!forPrint() && (dataSource?.loading$ | async)) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
}

<div fxFlex="1 1 auto" fxLayout="column" fxLayoutAlign="start stretch">
    <div class="search-title" fxLayout="row" fxLayoutAlign="space-between center" fxFlex="0 0 auto">
        <h5>{{ definition()?.displayname }}</h5>
        @if (!forPrint() && (dataSource?.count$ | async) > 0) {
            <div class="report-controls no-print" fxLayout="row" fxLayoutAlign="end center">
                <button mat-icon-button
                        matTooltip="Download Excel"
                        (click)="downloadExcel()">
                    <mat-icon svgIcon="download"></mat-icon>
                </button>
                <dash-gis-print-search [disabled]="definition().disablePrint"
                                       [inputs]="printComponentInputs"></dash-gis-print-search>
            </div>
        }
    </div>
    @if ((dataSource?.count$ | async) == 0) {
        <h2>No results found</h2>
    }
    <div class="table-container" fxFlex="1 1 auto">
        @if (definition()) {
            <table mat-table #table matSort
                   (matSortChange)="onSort($event)"
                   [dataSource]="dataSource">
                @if (mapEnabled && !forPrint()) {
                    <ng-container matColumnDef="zoom">
                        <th mat-header-cell *matHeaderCellDef>Zoom</th>
                        <td mat-cell *matCellDef="let fieldData">
                            <dash-gis-zoom-button [zoomLayer]="fieldData['DB_LAYER_ID'] || definition().layer"
                                                  [zoomObjectKey]="fieldData['DB_FEATURE_ID']"
                                                  [zoomObjectField]="definition().objectkey"></dash-gis-zoom-button>
                        </td>
                    </ng-container>
                }
                @for (resultField of definition().resultFields; track resultField.name) {
                    <ng-container matColumnDef="{{resultField.name}}">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ resultField.displayName }}</th>
                        <td mat-cell *matCellDef="let fieldData">
                            @switch (resultField.type) {
                                @case ('string') {
                                    {{ fieldData[resultField.name] }}
                                }
                                @case ('int') {
                                    {{ fieldData[resultField.name] }}
                                }
                                @case ('float') {
                                    {{ fieldData[resultField.name] | number: '1.2-5' }}
                                }
                                @case ('date') {
                                    {{ fieldData[resultField.name] | date:'yyyy-MM-dd' }}
                                }
                                @default {
                                    {{ resultField.type }}
                                }
                            }
                        </td>
                    </ng-container>
                }
                <!-- totals -->
                @for (fieldName of displayedColumns(); track fieldName; let i = $index) {
                    <ng-container
                        [matColumnDef]="'total_' + fieldName">
                        @if (definition().totalFields) {
                            @switch (getTotalsCellDisplay(fieldName, i)) {
                                @case ('label') {
                                    <td mat-footer-cell class="label" *matFooterCellDef>Totals:</td>
                                }
                                @case ('value') {
                                    <td mat-footer-cell class="value" *matFooterCellDef>
                                        @if (dataSource.totals$ | async; as totals) {
                                            {{ totalsValue(totals, fieldName) | number: '1.0-2' }}
                                        }
                                    </td>
                                }
                                @default {
                                    <td mat-footer-cell class="default" *matFooterCellDef></td>
                                }
                            }
                        }
                    </ng-container>
                }
                <!-- averages -->
                @for (fieldName of displayedColumns(); track fieldName; let i = $index) {
                    <ng-container
                        [matColumnDef]="'average_' + fieldName">
                        @if (definition().averageFields) {
                            @switch (getAveragesCellDisplay(fieldName, i)) {
                                @case ('label') {
                                    <td mat-footer-cell *matFooterCellDef>Averages:</td>
                                }
                                @case ('value') {
                                    <td mat-footer-cell *matFooterCellDef>
                                        @if (dataSource.averages$ | async; as averages) {
                                            {{ averagesValue(averages, fieldName) | number: '1.0-2' }}
                                        }
                                    </td>
                                }
                                @default {
                                    <td mat-footer-cell *matFooterCellDef></td>
                                }
                            }
                        }
                    </ng-container>
                }
                <tr mat-header-row *matHeaderRowDef="displayedColumns(); sticky: true"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns()"></tr>
                @if (definition().totalFields) {
                    <tr mat-footer-row *matFooterRowDef="totalsColumnsToDisplay"></tr>
                }
                @if (definition().averageFields) {
                    <tr mat-footer-row *matFooterRowDef="averagesColumnsToDisplay"></tr>
                }
            </table>
        }
    </div>
    @if (!forPrint()) {
        <mat-paginator [hidePageSize]="(dataSource?.count$ | async) <= paging()?.limit"
                       [pageSize]="paging()?.limit"
                       [pageIndex]="paging()?.page"
                       [pageSizeOptions]="pageOptions.values"
                       [length]="dataSource?.count$ | async"
                       (page)="onPage($event)" fxFlex="0 0 48px">
        </mat-paginator>
    }
</div>
